import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import { ChatProvider } from './context/ChatContext';
import { NavRefreshProvider, useNavRefresh } from './context/NavRefreshContext';
import { NAV_ITEMS } from './config/navigation';
import ModuleLocked from './components/ModuleLocked';
import { getOperatorToken } from './pages/operator/operatorApi';

// Loading component for Suspense fallback
const PageLoader = () => (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600">Laden...</span>
        </div>
    </div>
);

// Lazy loaded pages - Root
const Login = lazy(() => import('./pages/Login'));
const ClubRegister = lazy(() => import('./pages/ClubRegister'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const EmailConfirm = lazy(() => import('./pages/EmailConfirm'));
const OperatorLogin = lazy(() => import('./pages/operator/OperatorLogin'));
const OperatorResetPassword = lazy(() => import('./pages/operator/OperatorResetPassword'));
const OperatorDashboard = lazy(() => import('./pages/operator/OperatorDashboard'));
const JoinPage = lazy(() => import('./pages/JoinPage'));
const CompleteRegistration = lazy(() => import('./pages/CompleteRegistration'));
const ClubBilling = lazy(() => import('./pages/ClubBilling'));
const CancelSubscription = lazy(() => import('./pages/CancelSubscription'));
const PendingPayment = lazy(() => import('./pages/PendingPayment'));
const PaymentSuccess = lazy(() => import('./pages/PaymentResult').then(m => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import('./pages/PaymentResult').then(m => ({ default: m.PaymentCancel })));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Tasks = lazy(() => import('./pages/Tasks'));
const TaskDetail = lazy(() => import('./pages/TaskDetail'));
const Resources = lazy(() => import('./pages/Resources'));
const News = lazy(() => import('./pages/News'));
const Carpooling = lazy(() => import('./pages/Carpooling'));
const PublicCarpoolSignup = lazy(() => import('./pages/PublicCarpoolSignup'));
const PublicCarpoolGroupSignup = lazy(() => import('./pages/PublicCarpoolGroupSignup'));
const Documents = lazy(() => import('./pages/Documents'));

// Admin / Settings
const Admin = lazy(() => import('./pages/admin/Admin'));
const AddressBook = lazy(() => import('./pages/admin/AddressBook'));
const Invitations = lazy(() => import('./pages/admin/Invitations'));

// Sub-Modules
const Teams = lazy(() => import('./pages/teams/Teams'));
const TeamDetail = lazy(() => import('./pages/teams/TeamDetail'));
const TeamCreate = lazy(() => import('./pages/teams/TeamCreate'));
const Events = lazy(() => import('./pages/events/Events'));
const EventEdit = lazy(() => import('./pages/events/EventEdit'));
const EventDetail = lazy(() => import('./pages/events/EventDetail'));
const ICalSubscriptions = lazy(() => import('./pages/events/ICalSubscriptions'));

// Tournaments
const TournamentList = lazy(() => import('./pages/tournaments/TournamentList'));
const TournamentEdit = lazy(() => import('./pages/tournaments/TournamentEdit'));
const TournamentRegistrations = lazy(() => import('./pages/tournaments/TournamentRegistrations'));
const TournamentPlanner = lazy(() => import('./pages/tournaments/TournamentPlanner'));
const PublicTournament = lazy(() => import('./pages/tournaments/PublicTournament'));
const PublicLiveTournament = lazy(() => import('./pages/tournaments/PublicLiveTournament'));
const PublicLiveWall = lazy(() => import('./pages/tournaments/PublicLiveWall'));
const MultiRegistrationPublic = lazy(() => import('./pages/tournaments/MultiRegistrationPublic'));
const PublicResultEntry = lazy(() => import('./pages/tournaments/PublicResultEntry'));
const FieldResultEntry = lazy(() => import('./pages/tournaments/FieldResultEntry'));
const RefereeResultEntry = lazy(() => import('./pages/tournaments/RefereeResultEntry'));

// Services
const Services = lazy(() => import('./pages/services/Services'));
const ServiceReports = lazy(() => import('./pages/services/ServiceReports'));
const PublicService = lazy(() => import('./pages/services/PublicService'));
const PublicMultiService = lazy(() => import('./pages/services/PublicMultiService'));
const PublicShiftExchange = lazy(() => import('./pages/services/PublicShiftExchange'));

// Facility
const Facility = lazy(() => import('./pages/facility/Facility'));
const Inventory = lazy(() => import('./pages/facility/Inventory'));
const Defects = lazy(() => import('./pages/facility/Defects'));

// Meetings
const Meetings = lazy(() => import('./pages/meetings/Meetings'));
const MeetingDetail = lazy(() => import('./pages/meetings/MeetingDetail'));

// Chat
const Chat = lazy(() => import('./pages/chat/Chat'));

// Profile
const Profile = lazy(() => import('./pages/Profile'));
const ParentPortal = lazy(() => import('./pages/ParentPortal'));

// Live Ticker
const Ticker = lazy(() => import('./pages/ticker/Ticker'));
const TickerMatch = lazy(() => import('./pages/ticker/TickerMatch'));

// Legal
const Impressum = lazy(() => import('./pages/legal/Impressum'));
const Datenschutz = lazy(() => import('./pages/legal/Datenschutz'));

const OperatorRoute = ({ children }) => {
    if (!getOperatorToken()) return <Navigate to="/operator/login" replace />;
    return children;
};

const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    const { hasModule, entitlementsLoaded, entitlements } = useSettings();
    const location = useLocation();
    if (loading) return <div>Laden...</div>;
    if (!user) {
        // Fresh device (never logged in) -> club registration is the foreground.
        // Once any user has logged in on this device -> login is the foreground.
        const seenLogin = localStorage.getItem('has_logged_in_before');
        return <Navigate to={seenLogin ? '/login' : '/register-club'} replace />;
    }

    // Paywall: a club that chose "pay now" stays 'pending_payment' until the
    // Stripe checkout completes; a cancelled/expired club becomes 'suspended'.
    // Block the whole app (except the Stripe return routes) so access is locked
    // until (re)payment.
    const isPaymentReturn = location.pathname.startsWith('/payment/');
    const blockedStatus = ['pending_payment', 'suspended', 'canceled'].includes(entitlements?.club?.status);
    if (entitlementsLoaded && blockedStatus && !isPaymentReturn) {
        return <PendingPayment />;
    }

    // SaaS plan gating: block modules not included in the club's plan.
    const matched = NAV_ITEMS.find(i => i.module && (
        location.pathname === i.path || (i.path !== '/' && location.pathname.startsWith(i.path))
    ));
    // /resources and /ressourcen alias to the 'resources' module too.
    let lockedModule = matched?.module;
    if (!lockedModule && (location.pathname.startsWith('/resources') || location.pathname.startsWith('/ressourcen'))) {
        lockedModule = 'resources';
    }
    if (lockedModule) {
        if (!entitlementsLoaded) return <PageLoader />;
        if (!hasModule(lockedModule)) return <ModuleLocked module={lockedModule} />;
    }
    return children;
};

const getPageLabel = (pathname) => {
    if (pathname === '/login') return 'Login';
    if (pathname === '/') return 'Dashboard';
    if (pathname.startsWith('/events/new')) return 'Neues Event';
    if (pathname.startsWith('/events/') && pathname.endsWith('/edit')) return 'Event bearbeiten';
    if (pathname.startsWith('/events')) return 'Kalender';
    if (pathname.startsWith('/teams/')) return 'Teamdetails';
    if (pathname.startsWith('/teams')) return 'Teams';
    if (pathname.startsWith('/resources') || pathname.startsWith('/bookings') || pathname.startsWith('/ressourcen')) return 'Ressourcen';
    if (pathname.startsWith('/carpooling')) return 'Fahrgemeinschaften';
    if (pathname.startsWith('/tasks')) return 'Aufgaben';
    if (pathname.startsWith('/documents')) return 'Dokumente';
    if (pathname.startsWith('/admin') || pathname.startsWith('/users') || pathname.startsWith('/settings')) return 'Verwaltung';
    if (pathname.startsWith('/profile')) return 'Profil';
    if (pathname.startsWith('/billing') || pathname.startsWith('/payment')) return 'Abo & Rechnungen';
    if (pathname.startsWith('/chat')) return 'Chat';
    if (pathname.startsWith('/meetings')) return 'Sitzungen';
    if (pathname.startsWith('/tournaments')) return 'Turniere';
    return 'Vereinsportal';
};

const NEUTRAL_APP_NAME = 'VereinManager';

const setFavicon = (href) => {
    let link = document.querySelector("link[rel='icon']") || document.querySelector("link[rel='shortcut icon']");
    if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'icon');
        document.head.appendChild(link);
    }
    link.setAttribute('rel', 'icon');
    link.setAttribute('href', href);
};

const RouteTitleManager = () => {
    const location = useLocation();
    const { settings } = useSettings();
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        const neutralFavicon = `${process.env.PUBLIC_URL || ''}/favicon.ico`;
        // The operator/platform area must ALWAYS be brand-neutral — never leak a
        // club's name or crest into the browser tab, even if a club session is active.
        const isOperator = location.pathname.startsWith('/operator');

        if (isOperator) {
            document.title = `Betreiber-Bereich | ${NEUTRAL_APP_NAME}`;
            setFavicon(neutralFavicon);
            return;
        }

        const clubName = settings?.club_name || NEUTRAL_APP_NAME;
        document.title = `${getPageLabel(location.pathname)} | ${clubName}`;

        // Each club only ever sees its own crest; without a tenant logo stay neutral.
        if (settings?.logo_url) {
            const logoPath = settings.logo_url.startsWith('http')
                ? settings.logo_url
                : `${BACKEND_URL}${settings.logo_url}`;
            const version = settings.updated_at || settings.logo_url;
            setFavicon(`${logoPath}?v=${encodeURIComponent(version)}`);
        } else {
            setFavicon(neutralFavicon);
        }
    }, [location.pathname, settings?.club_name, settings?.logo_url, settings?.updated_at, BACKEND_URL]);

    return null;
};

const App = () => {
    return (
        <Router>
            <AuthProvider>
                <SettingsProvider>
                    <ChatProvider>
                        <NavRefreshProvider>
                        <RouteTitleManager />
                        <Suspense fallback={<PageLoader />}>
                            <AppRoutes />
                        </Suspense>
                        </NavRefreshProvider>
                    </ChatProvider>
                </SettingsProvider>

            </AuthProvider>
        </Router>
    );
};

// Keyed by navNonce so any sidebar/bell navigation (even to the current page)
// remounts the matched route and re-fetches fresh data.
const AppRoutes = () => {
    const { navNonce } = useNavRefresh();
    return (
        <Routes key={navNonce}>
                                {/* Public */}
                                <Route path="/login" element={<Login />} />
                                <Route path="/register-club" element={<ClubRegister />} />
                                <Route path="/verein-registrieren" element={<ClubRegister />} />
                                <Route path="/operator/login" element={<OperatorLogin />} />
                                <Route path="/operator/reset-password" element={<OperatorResetPassword />} />
                                <Route path="/email-bestaetigen" element={<EmailConfirm />} />
                                <Route path="/operator" element={<OperatorRoute><OperatorDashboard /></OperatorRoute>} />
                                <Route path="/beitritt/:token" element={<JoinPage />} />
                                <Route path="/complete-registration/:token" element={<CompleteRegistration />} />
                                <Route path="/public/tournament/:id" element={<PublicTournament />} />
                                <Route path="/public/tournament/:id/live" element={<PublicLiveTournament />} />
                                <Route path="/public/live-page/:slug" element={<PublicLiveWall />} />
                                <Route path="/public/live-auto" element={<PublicLiveWall autoMode />} />
                                <Route path="/anmeldung/:slug" element={<MultiRegistrationPublic />} />
                                <Route path="/ergebnis/:token" element={<PublicResultEntry />} />
                                <Route path="/feld/:token" element={<FieldResultEntry />} />
                                <Route path="/schiri/:token" element={<RefereeResultEntry />} />
                                <Route path="/public/service/:slug" element={<PublicService />} />
                                <Route path="/public/dienst-abgeben/:token" element={<PublicShiftExchange />} />
                                <Route path="/public/services/:slug" element={<PublicMultiService />} />
                                <Route path="/public/carpool/:slug" element={<PublicCarpoolSignup />} />
                                <Route path="/public/carpool/group/:slug" element={<PublicCarpoolGroupSignup />} />
                                
                                {/* Protected */}
                                <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                                <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                                
                                <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
                                <Route path="/tasks/:taskId" element={<ProtectedRoute><TaskDetail /></ProtectedRoute>} />
                                <Route path="/bookings" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
                                <Route path="/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
                                <Route path="/ressourcen" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
                                <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
                                
                                {/* Admin & Settings map to Admin component */}
                                <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                                <Route path="/users" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                                <Route path="/settings" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                                <Route path="/billing" element={<ProtectedRoute><ClubBilling /></ProtectedRoute>} />
                                <Route path="/billing/cancel" element={<ProtectedRoute><CancelSubscription /></ProtectedRoute>} />
                                <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                                <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
                                <Route path="/my-children" element={<ProtectedRoute><ParentPortal /></ProtectedRoute>} />
                                <Route path="/addressbook" element={<ProtectedRoute><AddressBook /></ProtectedRoute>} />
                                <Route path="/invitations" element={<ProtectedRoute><Invitations /></ProtectedRoute>} />
                                
                                <Route path="/news" element={<ProtectedRoute><News /></ProtectedRoute>} />
                                
                                <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
                                <Route path="/teams/new" element={<ProtectedRoute><TeamCreate /></ProtectedRoute>} />
                                <Route path="/teams/:id" element={<ProtectedRoute><TeamDetail /></ProtectedRoute>} />
                                
                                <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
                                <Route path="/events/new" element={<ProtectedRoute><EventEdit /></ProtectedRoute>} />
                                <Route path="/events/ical" element={<ProtectedRoute><ICalSubscriptions /></ProtectedRoute>} />
                                <Route path="/events/:id" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
                                <Route path="/events/:id/edit" element={<ProtectedRoute><EventEdit /></ProtectedRoute>} />
                                
                                {/* Tournaments */}
                                <Route path="/tournaments" element={<ProtectedRoute><TournamentList /></ProtectedRoute>} />
                                <Route path="/tournaments/new" element={<ProtectedRoute><TournamentEdit /></ProtectedRoute>} />
                                <Route path="/tournaments/:id" element={<ProtectedRoute><TournamentEdit /></ProtectedRoute>} />
                                <Route path="/tournaments/:id/registrations" element={<ProtectedRoute><TournamentRegistrations /></ProtectedRoute>} />
                                <Route path="/tournaments/:id/planner" element={<ProtectedRoute><TournamentPlanner /></ProtectedRoute>} />
                                
                                {/* Services */}
                                <Route path="/services" element={<ProtectedRoute><Services /></ProtectedRoute>} />
                                <Route path="/services/reports" element={<ProtectedRoute><ServiceReports /></ProtectedRoute>} />
                                
                                <Route path="/carpooling" element={<ProtectedRoute><Carpooling /></ProtectedRoute>} />
                                
                                {/* Facility */}
                                <Route path="/facility" element={<ProtectedRoute><Facility /></ProtectedRoute>} />
                                <Route path="/facility/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
                                <Route path="/facility/defects" element={<ProtectedRoute><Defects /></ProtectedRoute>} />
                                
                                {/* Meetings */}
                                <Route path="/meetings" element={<ProtectedRoute><Meetings /></ProtectedRoute>} />
                                <Route path="/meetings/sessions/:id" element={<ProtectedRoute><MeetingDetail /></ProtectedRoute>} />
                                
                                <Route path="/impressum" element={<ProtectedRoute><Impressum /></ProtectedRoute>} />
                                <Route path="/datenschutz" element={<ProtectedRoute><Datenschutz /></ProtectedRoute>} />
                                {/* Chat */}
                                <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
                                {/* Live Ticker */}
                                <Route path="/ticker" element={<ProtectedRoute><Ticker /></ProtectedRoute>} />
                                <Route path="/ticker/:id" element={<ProtectedRoute><TickerMatch /></ProtectedRoute>} />
                            </Routes>
    );
};

export default App;
